<script lang="ts" setup>
	import { useUser } from "@/stores/user";
	import { type IExport } from "~/server/models/export";
	import { Toaster } from "vue-sonner";
	import "vue-sonner/style.css";
	import { useIsPhone } from "@/composables/useIsPhone";
	import { useFiltersSheet } from "@/composables/useFiltersSheet";
	import { useMobileMenu } from "@/composables/useMobileMenu";
	import { useLayoutPreference } from "@/composables/useLayoutPreference";

	// A phone user who chose the desktop layout gets a desktop-width viewport,
	// which is what flips every width-based rule to the desktop version.
	const layoutPreference = useLayoutPreference();

	useHead({
		title: "LeadX",
		meta: [
			{ name: "description", content: "" },
			{ name: "viewport", content: layoutPreference.viewportContent },
		],
	});

	const userStore = useUser();
	const { load: loadUser } = userStore;
	const route = useRoute();

	// Toasts sit bottom-left on desktop, where nothing else lives. On a phone that
	// corner is the tab bar, so they drop in from the top instead.
	const isPhone = useIsPhone();
	const toasterPosition = computed(() => (isPhone.value ? "top-center" : "bottom-left"));

	// The phone sheets (filters, menu) are app state, so the one component that
	// outlives every page closes them on navigation. A page's own watcher can be
	// torn down mid-transition before it fires.
	const filtersSheet = useFiltersSheet();
	const mobileMenu = useMobileMenu();
	watch(
		() => route.path,
		() => {
			filtersSheet.close();
			mobileMenu.close();
		}
	);

	const exportPollIntervalMs = 5000;
	const userRefreshIntervalMs = 60_000;
	const isCheckingExports = ref(false);
	const isRefreshingUser = ref(false);
	const statusSnapshotInitialized = ref(false);
	const lastStatuses = ref<Map<string, string>>(new Map());
	let exportPollTimer: ReturnType<typeof setInterval> | null = null;
	let userRefreshTimer: ReturnType<typeof setInterval> | null = null;

	const normalizeStatus = (value?: string | null) => {
		if (!value) return "";
		return value.trim().toUpperCase();
	};

	const isCompleteStatus = (value?: string | null) => {
		if (!value) return false;
		return normalizeStatus(value) === "COMPLETE";
	};

	const shouldMonitorExports = computed(() => {
		const user = userStore.user;
		if (!userStore.isAuthenticated || !user) return false;
		const canAccessExports = user.access_companies === true || user.access_ucc === true;
		if (!canAccessExports) return false;
		return !route.path.startsWith("/exports");
	});

	const shouldRefreshUserPeriodically = computed(() => {
		return userStore.isAuthenticated === true;
	});

	async function refreshCurrentUser() {
		if (isRefreshingUser.value || !shouldRefreshUserPeriodically.value) return;
		isRefreshingUser.value = true;

		try {
			await userStore.load(true);
		} catch (error) {
			console.error("Failed to refresh user credits:", error);
		} finally {
			isRefreshingUser.value = false;
		}
	}

	const hasCompletedTransition = (list: IExport[]) => {
		const nextStatuses = new Map<string, string>();
		let completedTransition = false;

		list.forEach((item) => {
			const id = item.id?.trim();
			if (!id) return;

			const nextStatus = normalizeStatus(item.status);
			nextStatuses.set(id, nextStatus);

			if (!statusSnapshotInitialized.value) return;
			const previousStatus = lastStatuses.value.get(id);
			if (!previousStatus) return;

			if (!isCompleteStatus(previousStatus) && isCompleteStatus(nextStatus)) {
				completedTransition = true;
			}
		});

		lastStatuses.value = nextStatuses;
		statusSnapshotInitialized.value = true;
		return completedTransition;
	};

	async function checkExportStatuses() {
		if (isCheckingExports.value || !shouldMonitorExports.value) return;
		isCheckingExports.value = true;

		try {
			const res = await query<IExport>("exports");
			if (hasCompletedTransition(res.list)) {
				await refreshCurrentUser();
			}
		} catch (error) {
			console.error("Failed to poll export statuses:", error);
		} finally {
			isCheckingExports.value = false;
		}
	}

	function stopExportPolling() {
		if (!exportPollTimer) return;
		clearInterval(exportPollTimer);
		exportPollTimer = null;
	}

	function startExportPolling() {
		if (!process.client || exportPollTimer || !shouldMonitorExports.value) return;
		exportPollTimer = setInterval(() => {
			void checkExportStatuses();
		}, exportPollIntervalMs);
		void checkExportStatuses();
	}

	function stopUserRefreshPolling() {
		if (!userRefreshTimer) return;
		clearInterval(userRefreshTimer);
		userRefreshTimer = null;
	}

	function startUserRefreshPolling() {
		if (!process.client || userRefreshTimer || !shouldRefreshUserPeriodically.value) return;
		userRefreshTimer = setInterval(() => {
			void refreshCurrentUser();
		}, userRefreshIntervalMs);
		void refreshCurrentUser();
	}

	if (process.server) {
		await loadUser();
	}

	if (process.client) {
		watch(
			shouldMonitorExports,
			(shouldMonitor) => {
				if (shouldMonitor) {
					startExportPolling();
					return;
				}
				stopExportPolling();
			},
			{ immediate: true }
		);

		watch(
			shouldRefreshUserPeriodically,
			(shouldRefresh) => {
				if (shouldRefresh) {
					startUserRefreshPolling();
					return;
				}
				stopUserRefreshPolling();
			},
			{ immediate: true }
		);

		watch(
			() => userStore.user?.id,
			(nextUserId, previousUserId) => {
				if (nextUserId === previousUserId) return;
				lastStatuses.value = new Map();
				statusSnapshotInitialized.value = false;
				if (shouldMonitorExports.value) {
					startExportPolling();
				}
			}
		);

		onBeforeUnmount(() => {
			stopExportPolling();
			stopUserRefreshPolling();
		});
	}
</script>

<template>
	<div class="authenticated" :class="{ 'sheet-open': filtersSheet.open.value }">
		<Navigation />

		<div class="content">
			<Header />

			<main><slot /></main>
			<Toaster :position="toasterPosition" />
			<ConfirmDialog />
		</div>

		<!-- Phone-only: replaces the sidebar. Part of the flex column rather than
		     fixed, so the scrolling <main> always ends above it and no page has to
		     reserve space for it. -->
		<MobileTabBar />
		<MobileMenuSheet />

		<CommandPalette />
	</div>
</template>

<style scoped lang="scss">
	.authenticated {
		// A quiet neutral canvas lets white surfaces read as surfaces and keeps
		// brand red for accents instead of washing every page in it.
		background: var(--bg-canvas);
		display: flex;
		width: 100%;
		// dvh tracks Safari's collapsing toolbars; the vh line is the fallback
		// for browsers without it. Without dvh the shell was taller than the
		// visible viewport on iPhone and the page scrolled under the toolbar.
		height: 100vh;
		height: 100dvh;
		max-height: 100vh;
		max-height: 100dvh;
		flex-direction: row;

		// Phone: header, page, tab bar stacked; the sidebar is display:none in
		// Navigation.vue. While the filter sheet is open the header and tab bar
		// are hidden outright, so the sheet is fully visible even in browsers
		// that keep <main> in its own stacking layer (iOS Safari).
		@include phone {
			flex-direction: column;

			&.sheet-open {
				.content > header,
				.mobile-tab-bar {
					visibility: hidden;
				}
			}
		}

		.content {
			display: flex;
			flex-direction: column;
			flex: 1 1 auto;
			min-width: 0;
			height: 100%;

			// Phone: the column is header + page + tab bar, so the page wrapper
			// takes whatever the tab bar leaves. With height: 100% it kept the
			// full shell height and the document overflowed by one tab bar,
			// which lets iOS rubber-band the whole app under the toolbar.
			@include phone {
				flex: 1 1 0;
				min-height: 0;
				height: auto;
			}
			header {
				flex-shrink: 0;
				z-index: 1000;

				// The phone filter sheet (z 150) has to cover the header, and the
				// taxonomy pickers opened from inside it (z 200) have to cover the
				// sheet. Nothing on a phone page reaches 100.
				@include phone {
					z-index: 100;
				}
			}

			main {
				position: relative;
				flex: 1 1 auto;
				min-height: 0;

				/* The brand wash is the app background, on every authenticated page.
				   It lives on <main> rather than on the shell or each page root so
				   that nested routes inherit it, it starts below the header, and it
				   stays anchored to the viewport instead of scrolling away with a
				   centred content column. */
				background:
					radial-gradient(72.29% 72.29% at 50% 0%, $red_semi 0%, rgba(255, 255, 255, 0) 100%),
					white;

				overflow-y: auto;
				width: 100%;
				display: flex;
				align-items: center;
				flex-direction: column;
				justify-content: flex-start;

				// No -webkit-overflow-scrolling: touch here. On iOS Safari it turns the
				// scroller into its own stacking layer, and the phone filter sheet
				// (position: fixed inside <main>) then paints under the header and tab
				// bar whatever its z-index.
				@include phone {
					overscroll-behavior-y: contain;
				}
			}
		}
	}
</style>
